import fp from 'lodash/fp';
import React from 'react';
import {
  Button,
  Col,
  Grid,
  OverlayTrigger,
  Row,
  Tooltip
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { LinkContainer } from 'react-router-bootstrap';
import { Redirect } from 'react-router-dom';
import {
  branch,
  compose,
  lifecycle,
  renderComponent,
  withProps,
  withStateHandlers
} from 'recompose';
import { bindActionCreators } from 'redux';
import { defaultUnselectedCpdCategorys, routes } from '../../../constants';
import { handleInvalidForm, isBusinessAdmin } from '../../../services';
import { globalActions } from '../../../state/modules/global';
import { sessionActions } from '../../../state/modules/session';
import { userActions } from '../../../state/modules/user';
import { Icon } from '../../components';
import { CpdCategorySelection } from '../../partials';
import Loading from '../loading';
import { systemStatusActions } from '../../../state/modules/system-status';
import FullScreenLoading from '../full-screen-loading';
import {
  filterNonLegacy,
  filterNonRmGoalCategory
} from '../../../common/cpd-category-filter';

const CpdCategoryForm = branch(
  ({ isLoading }) => isLoading,
  renderComponent(Loading)
)(
  ({
    cpdCategorys: cpdCategorysIds,
    componentRef,
    industry: { cpdCategorys: industryCpdCategorys },
    onSelectCpdCategory,
    validateForm,
    validationMessages,
    systemProfile
  }) => {
    const cpdCategoryIdsWithRmGoal = systemProfile.cpdCategoryGoals
      .filter(({ progressGoalName }) => progressGoalName === 'RM CPD')
      .map(({ cpdCategoryId }) => cpdCategoryId);

    const validIndustryCpdCategorys = fp.flow(
      filterNonLegacy,
      filterNonRmGoalCategory(cpdCategoryIdsWithRmGoal)
    )(industryCpdCategorys);

    return (
      <form
        ref={(form) => {
          componentRef && (componentRef.formRef = form);
        }}
        noValidate
        onSubmit={validateForm}
      >
        <fieldset>
          <CpdCategorySelection
            {...{
              cpdCategorysIds,
              industryCpdCategorys: validIndustryCpdCategorys,
              validationMessages,
              onSelectCpdCategory
            }}
          />
        </fieldset>
        <Row className='no-gutter submit-row'>
          <Col xs={12} md={6} mdOffset={3}>
            <Button type='submit' className='large'>
              Continue
            </Button>
            <p>
              <LinkContainer
                exact
                className='full'
                to={
                  isBusinessAdmin
                    ? routes.onboarding.teamInvite
                    : routes.app.dashboard
                }
              >
                <Button bsStyle='plain'>
                  {isBusinessAdmin
                    ? "I'll do this later - skip this step"
                    : "I'll do this later - take me to Caddie"}
                </Button>
              </LinkContainer>
            </p>
          </Col>
        </Row>
      </form>
    );
  }
);

const tooltip = (isBusinessAdmin, overlayRef) => (
  <Tooltip id='areas-of-advice-tooltip'>
    <Button
      onClick={() => overlayRef.hide()}
      className='pull-right full-width close-overlay'
      bsStyle='plain'
    >
      <Icon icon='close' />
    </Button>
    <p>
      <strong>CPD categories</strong>
    </p>
    <p>
      {isBusinessAdmin
        ? 'Your team’s default training plan should include all of the CPD categories your team are required to do CPD in, based on your AFSL requirements.'
        : 'Specify the knowledge areas you are permitted to provide general and personal advice for.'}
    </p>
  </Tooltip>
);

/**
 * Allows users to specify their initial areas of advice during on-boarding.
 */
const CpdCategorys = (props) => (
  <section id='onboarding'>
    <Grid>
      <Row>
        <Col xs={12} md={8} mdOffset={2}>
          <h1 className='text-center'>
            {props.isBusinessAdmin
              ? "Create your team's default training plan"
              : 'Select your CPD categories'}
          </h1>
          <p>
            {props.isBusinessAdmin
              ? 'Select the CPD categories your authorised representatives are licensed to give advice under. You can change this at any time.'
              : 'You can change these at any time'}
          </p>
          <p>
            <OverlayTrigger
              ref={(ref) => props.setOverlayRef(ref)}
              placement='bottom'
              overlay={tooltip(props.isBusinessAdmin, props.overlayRef)}
            >
              <Button bsStyle='plain'>
                <Icon icon='help' className='hide-on-ie' />{' '}
                <span>What's this?</span>
              </Button>
            </OverlayTrigger>
          </p>
        </Col>
      </Row>
      <Row>
        <Col xs={12} sm={10} smOffset={1}>
          <CpdCategoryForm {...props} />
        </Col>
      </Row>
    </Grid>
  </section>
);

const mapStateToProps = (state) => ({
  user: state.user,
  session: state.session,
  industry: state.industry,
  systemProfile: state.systemStatus.profile
});

const mapDispatchToProps = (dispatch) => ({
  logout: bindActionCreators(sessionActions.session.logout, dispatch),
  getUser: bindActionCreators(userActions.user.getCurrent, dispatch),
  getUserIndustryRoles: bindActionCreators(
    userActions.user.getIndustryRoles,
    dispatch
  ),
  getCpdCategorys: bindActionCreators(
    userActions.user.getCpdCategorys,
    dispatch
  ),
  selectCpdCategorys: bindActionCreators(
    globalActions.onboarding.selectCpdCategorys,
    dispatch
  ),
  getSystemProfile: bindActionCreators(
    systemStatusActions.systemStatus.profile,
    dispatch
  )
});

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  lifecycle({
    componentDidMount() {
      this.props.getSystemProfile();
      const {
        getUser,
        getUserIndustryRoles,
        logout,
        session: { isAuthenticated },
        user: {
          currentUser: { id: hasUser },
          currentUserIndustryRoles
        }
      } = this.props;

      this.setState({ componentRef: this });

      if (!isAuthenticated) {
        logout();
      } else {
        if (!hasUser) {
          getUser();
        }

        if (currentUserIndustryRoles.length === 0) {
          getUserIndustryRoles();
        }
      }
    }
  }),
  branch(
    ({ systemProfile }) => !systemProfile,
    renderComponent(FullScreenLoading)
  ),
  branch(
    ({ session: { isAuthenticated } }) => !isAuthenticated,
    renderComponent(() => <Redirect to={routes.login} />)
  ),
  withProps(
    ({
      industry: { cpdCategorysIsFetching },
      user: {
        currentUser,
        currentUserCpdCategorysIsFetching,
        currentUserIndustryRoles,
        currentUserIndustryRolesIsFetching,
        isFetching
      }
    }) => ({
      isLoading:
        isFetching ||
        currentUserCpdCategorysIsFetching ||
        currentUserIndustryRolesIsFetching ||
        cpdCategorysIsFetching,
      isBusinessAdmin: isBusinessAdmin(currentUser)
    })
  ),
  branch(
    ({ industry: { cpdCategorysIsFetching } }) => cpdCategorysIsFetching,
    renderComponent(Loading)
  ),
  withStateHandlers(
    ({ industry }) => ({
      cpdCategorys: fp.flow(
        fp.filter(({ parentCpdCategoryId }) => parentCpdCategoryId),
        fp.map(({ id }) => id)
      )(industry.cpdCategorys),
      validation: {},
      validationMessages: {},
      overlayRef: null
    }),
    {
      onSelectCpdCategory:
        ({ cpdCategorys }) =>
        (cpdCategoryId) => ({
          cpdCategorys: fp.contains(cpdCategoryId, cpdCategorys)
            ? fp.without([cpdCategoryId], cpdCategorys)
            : fp.concat([cpdCategoryId], cpdCategorys)
        }),
      validateForm:
        (
          { cpdCategorys },
          {
            industry: { cpdCategorys: industryCpdCategorys },
            componentRef,
            selectCpdCategorys
          }
        ) =>
        (e) => {
          e.preventDefault();
          e.stopPropagation();

          const defaultSelectedCpdCategorys = fp.flow(
            fp.filter(
              ({ name }) => !fp.contains(name)(defaultUnselectedCpdCategorys)
            ),
            fp.map(({ id }) => id)
          )(industryCpdCategorys);

          const mandatoryCpdCategorys = fp.flow(
            fp.filter(({ mandatory }) => mandatory),
            fp.map(({ id }) => id)
          )(industryCpdCategorys);
          const selectedCpdCategorys =
            cpdCategorys.length === 0
              ? defaultSelectedCpdCategorys
              : cpdCategorys.concat(mandatoryCpdCategorys);

          const validation = {
            selectedCpdCategorys: selectedCpdCategorys.length > 0
          };
          const isValid = fp.all(fp.identity)(validation);

          if (isValid) {
            selectCpdCategorys(
              selectedCpdCategorys,
              routes.onboarding.cpdRequirements
            );
          } else {
            if (componentRef && componentRef.formRef) {
              handleInvalidForm(componentRef.formRef);
            }
          }

          return {
            validation,
            validationMessages: {
              selectedCpdCategorys: 'Please select at least one CPD category'
            }
          };
        },
      setOverlayRef: () => (ref) => ({ overlayRef: ref })
    }
  )
)(CpdCategorys);



// WEBPACK FOOTER //
// ./src/containers/pages/onboarding/areas-of-advice.js